import { Component, Input, OnInit } from '@angular/core';

import { BusinessService, StatKind, StatsReport } from '../../services/business.service';

interface Tile {
  kind: StatKind;
  label: string;
  hint?: string;
}

/**
 * SCRUM-267 — business performance report: list impressions, listing
 * opens, business-page visits, website clicks, phone taps and inquiries,
 * filterable by period and listing, paid shown apart from organic.
 *
 * Form: stat tiles for the headline numbers, one single-series bar chart
 * (listing opens per day) and a per-listing table. Bars carry a hover
 * tooltip; the table is the accessible view of the same data.
 */
@Component({
  selector: 'app-business-performance',
  templateUrl: './business-performance.component.html',
  styleUrls: ['./business-performance.component.scss'],
  standalone: false,
})
export class BusinessPerformanceComponent implements OnInit {
  /** Listings for the filter: [{id, name}]. */
  @Input() ads: Array<{ id: number; name: string }> = [];

  readonly periods = [7, 30, 90];
  period = 30;
  adId: number | null = null;

  report: StatsReport | null = null;
  loading = true;
  failed = false;
  hoverIndex: number | null = null;

  readonly tiles: Tile[] = [
    { kind: 'impression', label: 'הופעות ברשימות' },
    { kind: 'open', label: 'פתיחות מודעה' },
    { kind: 'page_visit', label: 'כניסות לעמוד העסק' },
    { kind: 'website_click', label: 'קליקים לאתר', hint: 'לא נספר כפנייה' },
    { kind: 'phone_tap', label: 'לחיצות לחיוג', hint: 'לחיצה — לא שיחה שהתקיימה' },
    { kind: 'inquiry', label: 'פניות' },
  ];

  constructor(private business: BusinessService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.failed = false;
    const to = new Date();
    const from = new Date(to.getTime() - (this.period - 1) * 86400000);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    this.business.stats({ from: iso(from), to: iso(to), ad: this.adId }).subscribe({
      next: (r) => {
        this.report = r;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.failed = true;
      },
    });
  }

  setPeriod(p: number): void {
    this.period = p;
    this.load();
  }

  onAdChange(value: string): void {
    this.adId = value ? Number(value) : null;
    this.load();
  }

  total(kind: StatKind): number {
    return this.report?.totals?.[kind]?.total ?? 0;
  }

  paid(kind: StatKind): number {
    return this.report?.totals?.[kind]?.paid ?? 0;
  }

  get series(): Array<{ day: string; value: number }> {
    return (this.report?.series || []).map((p) => ({ day: p.day, value: p.open || 0 }));
  }

  get maxValue(): number {
    return Math.max(1, ...this.series.map((p) => p.value));
  }

  barHeight(v: number): number {
    return v === 0 ? 0 : Math.max(4, Math.round((v / this.maxValue) * 100));
  }

  get hasData(): boolean {
    return this.tiles.some((t) => this.total(t.kind) > 0);
  }

  shortDay(iso: string): string {
    const [, m, d] = iso.split('-');
    return `${d}/${m}`;
  }
}
